import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { LogIn, ChevronRight, Menu, X, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const Navigation = () => {
  const navigate = useNavigate();
  const { session } = useUserProfile();
  const { profile } = useProfile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLoginClick = () => {
    navigate('/login', { state: { directLogin: true } });
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Sesión cerrada exitosamente");
      setIsMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toast.error("Error al cerrar sesión");
    }
  };

  const handleGetStarted = () => {
    if (session) {
      navigate('/chat');
    } else {
      navigate('/login');
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const getUserDisplayName = () => {
    console.log("getUserDisplayName - Profile data:", profile);
    console.log("getUserDisplayName - Session data:", session?.user);
    
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    } else if (profile?.first_name) {
      return profile.first_name;
    } else if (session?.user?.email) {
      return session.user.email.split('@')[0];
    }
    return 'Usuario';
  };

  // Public navigation items (always visible)
  const publicNavItems = [
    { label: 'Inicio', path: '/' },
    { label: 'Cómo funciona', path: '/how-it-works' },
    { label: '(FAQ)', path: '/faq' },
    { label: 'Quiénes somos', path: '/why-trust-us' },
    { label: 'Chat', path: '/chat' },
    { label: 'Blog', path: '/blog' }
  ];

  // Additional items for authenticated users
  const authenticatedNavItems = [
    { label: 'Sugerencias', path: '/user-feedback' },
    { label: 'Documentos', path: '/documents' }
  ];

  return (
    <header className="sticky top-0 z-50 w-full py-1 px-2 sm:px-4 bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <img 
            src="/lovable-uploads/04f1b910-a804-48d5-beda-abc0116fc18b.png" 
            alt="MigranteAmigo" 
            className="h-12 sm:h-14 lg:h-16 w-auto cursor-pointer"
            onClick={() => navigate('/')}
          />
        </div>

        {/* Centered Desktop Navigation Menu */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <nav className="flex items-center space-x-4">
            {/* Public navigation items */}
            {publicNavItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                variant="ghost"
                className="text-gray-600 hover:text-[#2563EB] transition-colors font-medium text-sm px-3 py-1.5 h-auto hover:bg-blue-50"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {item.label}
              </Button>
            ))}
            
            {/* Authenticated user navigation items */}
            {session && authenticatedNavItems.map((item) => (
              <Button
                key={item.path}
                onClick={() => navigate(item.path)}
                variant="ghost"
                className="text-gray-600 hover:text-[#2563EB] transition-colors font-medium text-sm px-3 py-1.5 h-auto hover:bg-blue-50"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                {item.label}
              </Button>
            ))}
          </nav>
        </div>

        {/* Right side CTA and Login Buttons - Desktop */}
        <div className="hidden lg:flex items-center space-x-2">
          <Button
            onClick={handleGetStarted}
            className="bg-[#2563EB] hover:bg-blue-700 text-white px-3 py-1.5 rounded-md font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Comienza
          </Button>
          
          {session ? (
            <>
              <Button 
                onClick={() => navigate('/chat')}
                variant="outline" 
                size="sm"
                className="flex items-center gap-1 text-gray-700 hover:text-[#2563EB] border-gray-300 hover:border-[#2563EB] hover:bg-blue-50 transition-all font-medium text-sm px-2 py-1.5 h-auto"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                <span>{getUserDisplayName()}</span>
                <ChevronRight className="h-3 w-3" />
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                size="sm"
                className="flex items-center gap-1 text-red-600 hover:text-red-700 border-red-300 hover:border-red-500 hover:bg-red-50 transition-all font-medium text-sm px-2 py-1.5 h-auto"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                <LogOut className="h-3 w-3" />
                Cerrar sesión
              </Button>
            </>
          ) : (
            <Button 
              onClick={handleLoginClick}
              variant="outline" 
              size="sm"
              className="flex items-center gap-1 text-gray-700 hover:text-[#2563EB] border-gray-300 hover:border-[#2563EB] hover:bg-blue-50 transition-all font-medium text-sm px-2 py-1.5 h-auto"
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <LogIn className="h-3 w-3" />
              Iniciar sesión
            </Button>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden flex items-center space-x-2">
          <Button
            onClick={handleGetStarted}
            className="bg-[#2563EB] hover:bg-blue-700 text-white px-3 py-1.5 rounded-md font-semibold text-sm"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Comienza
          </Button>
          
          {/* Mobile Hamburger Menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="p-2">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetHeader>
                <SheetTitle className="text-left">Menú</SheetTitle>
              </SheetHeader>
              
              <div className="flex flex-col space-y-4 mt-6">
                {/* Public Navigation Links */}
                {publicNavItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    variant="ghost"
                    className="justify-start text-gray-600 hover:text-[#2563EB] transition-colors font-medium text-base px-3 py-3 h-auto hover:bg-blue-50"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    {item.label}
                  </Button>
                ))}
                
                {/* Authenticated user navigation items */}
                {session && authenticatedNavItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    variant="ghost"
                    className="justify-start text-gray-600 hover:text-[#2563EB] transition-colors font-medium text-base px-3 py-3 h-auto hover:bg-blue-50"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    {item.label}
                  </Button>
                ))}
                
                {/* Divider */}
                <div className="border-t border-gray-200 my-4"></div>
                
                {/* Auth Section */}
                {session ? (
                  <>
                    <Button 
                      onClick={() => {
                        navigate('/chat');
                        setIsMobileMenuOpen(false);
                      }}
                      variant="outline" 
                      className="justify-start flex items-center gap-2 text-gray-700 hover:text-[#2563EB] border-gray-300 hover:border-[#2563EB] hover:bg-blue-50 transition-all font-medium text-base px-3 py-3 h-auto"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      <span>{getUserDisplayName()}</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button 
                      onClick={handleLogout}
                      variant="outline" 
                      className="justify-start flex items-center gap-2 text-red-600 hover:text-red-700 border-red-300 hover:border-red-500 hover:bg-red-50 transition-all font-medium text-base px-3 py-3 h-auto"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                    >
                      <LogOut className="h-4 w-4" />
                      Cerrar sesión
                    </Button>
                  </>
                ) : (
                  <Button 
                    onClick={handleLoginClick}
                    variant="outline" 
                    className="justify-start flex items-center gap-2 text-gray-700 hover:text-[#2563EB] border-gray-300 hover:border-[#2563EB] hover:bg-blue-50 transition-all font-medium text-base px-3 py-3 h-auto"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                  >
                    <LogIn className="h-4 w-4" />
                    Iniciar sesión
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
